// src/components/GenerateFileCard.tsx
//
// Renders in place of a ```pptx-json or ```pdf-json fence. Shows a summary
// card with a Download button that calls api/generate_file.php and saves
// the real .pptx/.pdf. For pptx, also offers a "Preview slides" toggle
// showing the SlideDeckPreview carousel before downloading.
//
// Reads the auth token straight from localStorage — same fallback pattern
// already used in ChatHistory.tsx / ProfileModal.tsx — so no new props need
// to be threaded through MessageBubble for this.
import React, { useMemo, useState } from 'react';
import { generateFile } from '../api';
import SlideDeckPreview from './SlideDeckPreview';

interface Props {
  type: 'pptx' | 'pdf' | 'docx' | 'xlsx';
  code: string; // raw JSON string
}

function getToken(): string | null {
  return localStorage.getItem('coderai_token');
}

function sanitizeForFilename(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9\-_ ]/g, '').trim();
  return cleaned || 'document';
}

function extensionFor(type: 'pptx' | 'pdf' | 'docx' | 'xlsx'): string {
  return type;
}

function iconFor(type: 'pptx' | 'pdf' | 'docx' | 'xlsx'): string {
  if (type === 'pptx') return '📊';
  if (type === 'docx') return '📝';
  if (type === 'xlsx') return '📈';
  return '📄';
}

function labelFor(type: 'pptx' | 'pdf' | 'docx' | 'xlsx'): string {
  if (type === 'pptx') return 'Presentation';
  if (type === 'docx') return 'Document';
  if (type === 'xlsx') return 'Spreadsheet';
  return 'Document';
}

export default function GenerateFileCard({ type, code }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const [spec, parseError] = useMemo((): [any, string | null] => {
    try {
      return [JSON.parse(code), null];
    } catch (e: any) {
      // Surface exactly where parsing failed, so a broken generation is
      // immediately actionable instead of a generic "invalid JSON" dead end.
      const msg: string = e?.message || 'Unknown parse error';
      const posMatch = /position (\d+)/.exec(msg);
      let snippet = '';
      if (posMatch) {
        const pos = parseInt(posMatch[1], 10);
        const start = Math.max(0, pos - 40);
        const end = Math.min(code.length, pos + 40);
        snippet = `\n\n…${code.slice(start, pos)}👉HERE👈${code.slice(pos, end)}…`;
      }
      return [null, msg + snippet];
    }
  }, [code]);

  if (!spec) {
    return (
      <div className="diagram-error">
        <p>⚠ Couldn't read this {type === 'pptx' ? 'presentation' : 'document'} spec — the generated JSON has a syntax error.</p>
        {parseError && <pre className="json-error-detail">{parseError}</pre>}
        <pre>{code}</pre>
      </div>
    );
  }

  const isPptx = type === 'pptx';
  const isXlsx = type === 'xlsx';
  const count = isPptx
    ? (spec.slides?.length || 0)
    : isXlsx
      ? (spec.sheets?.length || 0)
      : (spec.sections?.length || 0);
  const title = spec.title || labelFor(type);

  const handleDownload = async () => {
    setError('');
    setLoading(true);
    try {
      const blob = await generateFile({ type, content: code }, getToken());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeForFilename(title)}.${extensionFor(type)}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Failed to generate file.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="generate-file-wrap">
      <div className="generate-file-card">
        <div className="generate-file-icon">{iconFor(type)}</div>
        <div className="generate-file-info">
          <p className="generate-file-title">{title}</p>
          <p className="generate-file-meta">
            {isPptx
              ? `${count} slide${count === 1 ? '' : 's'}`
              : isXlsx
                ? `${count} sheet${count === 1 ? '' : 's'}`
                : `${count} section${count === 1 ? '' : 's'}`}
          </p>
        </div>
        {isPptx && (
          <button className="generate-file-btn secondary" onClick={() => setPreviewOpen(o => !o)}>
            {previewOpen ? 'Hide preview' : 'Preview slides'}
          </button>
        )}
        <button className="generate-file-btn" onClick={handleDownload} disabled={loading}>
          {loading ? <span className="gen-spinner" /> : `Download .${extensionFor(type)}`}
        </button>
      </div>
      {error && <p className="generate-file-error">{error}</p>}

      {isPptx && previewOpen && Array.isArray(spec.slides) && (
        <SlideDeckPreview title={spec.title} theme={spec.theme} slides={spec.slides} />
      )}
    </div>
  );
}